import React, { useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Collapse, Stack, Typography } from "@mui/material";
import type { DigestRow } from "store/types/news";
import { formatNewsDate } from "utils/format-news-date";

type Props = {
  rows: DigestRow[];
  onOpenHighlights: (row: DigestRow) => void;
};

export const DigestList: React.FC<Props> = ({ rows, onOpenHighlights }) => {
  const [expanded, setExpanded] = useState<string | false>(rows[0]?.date ?? false);
  const [expandSummary, setExpandSummary] = useState<string | false>(false);

  return (
    <Stack direction="column" sx={{ pt: "10px", gap: "4px" }}>
      {rows.map((row) => {
        const isOpen = expanded === row.date;
        const isSummaryExpanded = expandSummary === row.date;
        return (
          <Accordion
            key={row.date}
            expanded={isOpen}
            onChange={(_, v) => setExpanded(v ? row.date : false)}
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: isOpen ? "#26a6ae" : "#dbe8f5",
              borderRadius: "6px",
              bgcolor: "#fff",
              overflow: "hidden",
              "&:before": { display: "none" }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "#797979" }} />}
              sx={{
                px: "13px",
                py: "10px",
                minHeight: 0,
                bgcolor: isOpen ? "#e4f5f6" : "transparent",
                transition: "background-color 0.15s",
                "&:hover": { bgcolor: isOpen ? "#e4f5f6" : "#f6f8fc" },
                "& .MuiAccordionSummary-content": {
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  margin: 0,
                  overflow: "hidden"
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#797979",
                  transition: "transform 0.18s"
                },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)"
                }
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#071f35" }}>
                {formatNewsDate(row.date, { absoluteOnly: true })}
              </Typography>
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 400,
                  color: "#797979",
                  bgcolor: "#f6f8fc",
                  px: "7px",
                  py: "2px",
                  borderRadius: "100px",
                  flexShrink: 0,
                  lineHeight: 1.35
                }}
              >
                {row.highlights.length} highlights
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                px: "13px",
                pt: 0,
                pb: "12px",
                borderTop: `1px solid ${"#dbe8f5"}`
              }}
            >
              <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mt: "10px", display: "grid", gap: "4px" }}>
                {row.highlights.slice(0, 4).map((highlight, i) => (
                  <Box
                    component="li"
                    key={i}
                    sx={{
                      fontSize: 12,
                      color: "#5d7782",
                      pl: "13px",
                      position: "relative",
                      "&::before": {
                        // eslint-disable-next-line quotes
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "10px",
                        width: "4px",
                        height: "4px",
                        borderRadius: "50%",
                        bgcolor: "#c09560"
                      }
                    }}
                  >
                    {highlight}
                  </Box>
                ))}
              </Box>
              {row.summary && (
                <Collapse in={isSummaryExpanded}>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: "12px",
                      pt: "12px",
                      fontSize: 12,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      color: "#5d7782",
                      whiteSpace: "pre-wrap"
                    }}
                  >
                    {row.summary}
                  </Typography>
                </Collapse>
              )}
              <Stack direction="row" justifyContent="space-between" alignItems="center" mt="12px">
                {row.summary ? (
                  <Button
                    type="button"
                    variant="text"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandSummary((prev) => (prev === row.date ? false : row.date));
                    }}
                    startIcon={
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 16,
                          transition: "transform 0.18s",
                          transform: isSummaryExpanded ? "rotate(180deg)" : "none"
                        }}
                      />
                    }
                    sx={{
                      p: 0,
                      minHeight: 0,
                      fontSize: 12,
                      fontWeight: 500,
                      textTransform: "none",
                      color: "#5d7782",
                      "&:hover": { bgcolor: "transparent", color: "#071f35" }
                    }}
                  >
                    Full summary
                  </Button>
                ) : (
                  <Stack />
                )}
                <Stack direction="row" gap="8px" alignItems="center">
                  <Button
                    type="button"
                    size="sm"
                    variant="contained"
                    startIcon={<ListAltIcon sx={{ fontSize: 15 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenHighlights(row);
                    }}
                    sx={{
                      height: 28,
                      fontSize: 12,
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: "100px",
                      px: "13px",
                      color: "primary.contrastText",
                      border: "1px solid transparent"
                    }}
                  >
                    All highlights
                  </Button>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};
